import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useQuery, useUpdateQuery } from '../../../hooks/useQuery'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PRESS_RELAESES_FILTER_PARAMS, validateContentType, validateEndDate } from './utils'
import { endOfMonth } from 'date-fns'
import Autocomplete from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import { debounce } from '@mui/material/utils'
import { PressReleasesFilterContentType } from '@finance-lab/utils/types'
import ClickAwayListener from '@mui/material/ClickAwayListener'

const CONTENT_TYPE_LABELS: Record<PressReleasesFilterContentType, string> = {
    all: 'Visa allt',
    analyzeAndIr: 'Analys & IR',
    pressReleases: 'Pressmeddelanden',
    reports: 'Rapporter',
    relatedTransactions: 'Transaktioner',
} as const

const contentTypes = Object.keys(CONTENT_TYPE_LABELS) as PressReleasesFilterContentType[]

export const PressReleasesFilter = ({
    isSlim,
    isSingleDaySelect,
}: {
    isSingleDaySelect?: boolean
    isSlim?: boolean
}) => {
    const query = useQuery()
    const updateQuery = useUpdateQuery()
    const [isOpen, setIsOpen] = useState(false)
    const contentType = validateContentType(query.get(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE))
    const endDate = validateEndDate(query.get(PRESS_RELAESES_FILTER_PARAMS.END_DATE))

    const handleChangeEndDate = (value: Date | null) => {
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.END_DATE, formatDateYYYYMMDD(value, true))
    }

    const handleChangeSearchValue = useMemo(
        () =>
            debounce((value: string) => {
                updateQuery(PRESS_RELAESES_FILTER_PARAMS.SEARCH, value)
            }),
        [updateQuery],
    )

    const handleChangeContentType = (value: PressReleasesFilterContentType) => {
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE, value)
    }

    return (
        <Grid container spacing={2}>
            <Grid size={6}>
                <Box sx={{ marginY: 2 }}>
                    <FormControl fullWidth>
                        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                            <Autocomplete<PressReleasesFilterContentType, true, true>
                                autoFocus
                                disableClearable
                                options={contentTypes}
                                getOptionLabel={(v) => CONTENT_TYPE_LABELS[v]}
                                limitTags={1}
                                open={isOpen}
                                onClose={() => setIsOpen(false)}
                                onOpen={() => setIsOpen(true)}
                                renderValue={(value) => {
                                    const numTags = value.length

                                    if (value.length > 1) return '2 valda'

                                    return (
                                        <Typography variant='body2'>
                                            {value
                                                .slice(0, 1)
                                                .map((option) => CONTENT_TYPE_LABELS[option])
                                                .join(', ')}

                                            {numTags > 1 && ` +${numTags - 1}`}
                                        </Typography>
                                    )
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} sx={{}} label='Innehåll' onClick={() => setIsOpen(true)} />
                                )}
                                onChange={(e, v) => handleChangeContentType(v)}
                                size={isSlim ? 'small' : undefined}
                                sx={{
                                    width: '100%',
                                    '.MuiInputBase-root': { cursor: 'pointer' },
                                    '.MuiInputBase-input': { visibility: 'hidden' },
                                }}
                                value={[contentType]}
                            />
                        </ClickAwayListener>
                    </FormControl>
                </Box>
            </Grid>

            <Grid size={6}>
                <Box sx={{ marginY: 2 }}>
                    <DatePicker
                        label={isSingleDaySelect ? 'Tidpunkt' : 'Tidsperiod slut'}
                        views={isSingleDaySelect ? ['year', 'month', 'day'] : ['year', 'month']}
                        format={'dd MMM yy'}
                        minDate={new Date('2015-01-01')}
                        maxDate={new Date()}
                        onChange={(newValue) => {
                            const d = newValue ?? new Date()

                            handleChangeEndDate(isSingleDaySelect ? d : endOfMonth(d))
                        }}
                        slotProps={{
                            textField: {
                                size: isSlim ? 'small' : undefined,
                            },
                        }}
                        value={new Date(endDate)}
                    />
                </Box>
            </Grid>

            <Grid size={12}>
                <Box>
                    <TextField
                        label='Fritextsökning'
                        size={isSlim ? 'small' : undefined}
                        onChange={(event) => handleChangeSearchValue(event.target.value)}
                    />
                </Box>
            </Grid>
        </Grid>
    )
}
